import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Play, GitBranch, ArrowDown, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Workflows() {
  const [items, setItems] = useState([]);
  const [agents, setAgents] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [steps, setSteps] = useState([{ agent_id: "sales", prompt: "", label: "" }]);
  const [running, setRunning] = useState(null);
  const [result, setResult] = useState(null);

  const load = () => {
    api.get("/workflows").then((r) => setItems(r.data.workflows));
    api.get("/agents").then((r) => setAgents(r.data.agents.filter((a) => a.status === "active")));
  };
  useEffect(() => { load(); }, []);

  const addStep = () => setSteps([...steps, { agent_id: "sales", prompt: "", label: "" }]);
  const rmStep = (i) => setSteps(steps.filter((_, idx) => idx !== i));
  const upStep = (i, k, v) => setSteps(steps.map((s, idx) => idx === i ? { ...s, [k]: v } : s));

  const create = async () => {
    if (!title || steps.some((s) => !s.prompt)) return toast.error("Título y prompts requeridos");
    try {
      await api.post("/workflows", { title, description, steps });
      toast.success("Workflow creado");
      setDlg(false);
      setTitle(""); setDescription(""); setSteps([{ agent_id: "sales", prompt: "", label: "" }]);
      load();
    } catch { toast.error("Fallo"); }
  };

  const run = async (w) => {
    setRunning(w.id); setResult(null);
    try {
      const { data } = await api.post(`/workflows/${w.id}/run`, { input_context: "" });
      setResult(data);
      toast.success(`Ejecutado · ${data.outputs.length} pasos`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fallo");
    } finally { setRunning(null); }
  };

  return (
    <div className="p-8 space-y-6" data-testid="workflows-page">
      <div className="border-b border-[var(--border)] pb-5 flex items-end justify-between">
        <div>
          <div className="label-mono mb-2">PIPELINES MULTI-AGENTE</div>
          <h1 className="font-display font-black text-4xl tracking-tight">Workflows</h1>
          <p className="text-sm text-[var(--text-dim)] mt-1">
            Encadena varios agentes. La salida de cada paso se inyecta al siguiente.
          </p>
        </div>
        <Dialog open={dlg} onOpenChange={setDlg}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="wf-add">
              <Plus className="w-4 h-4 mr-2" /> Nuevo workflow
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none bg-[var(--surface)] border-[var(--border)] max-w-3xl max-h-[80vh] overflow-auto">
            <DialogHeader><DialogTitle className="font-display">Nuevo workflow</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="label-mono">Título</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="wf-title" />
              </div>
              <div>
                <Label className="label-mono">Descripción</Label>
                <Input value={description} onChange={(e) => setDescription(e.target.value)} className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="wf-desc" />
              </div>
              <div className="label-mono pt-2">PASOS</div>
              {steps.map((s, i) => (
                <div key={i} className="border border-[var(--border)] p-3 space-y-2" data-testid={`wf-step-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-[var(--accent)]">PASO {i + 1}</span>
                    {steps.length > 1 && (
                      <button onClick={() => rmStep(i)} className="text-[var(--danger)]"><Trash2 className="w-3 h-3" /></button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Select value={s.agent_id} onValueChange={(v) => upStep(i, "agent_id", v)}>
                      <SelectTrigger className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono text-xs" data-testid={`wf-agent-${i}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none bg-[var(--surface)] border-[var(--border)] max-h-64">
                        {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input value={s.label} onChange={(e) => upStep(i, "label", e.target.value)} placeholder="Etiqueta"
                           className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono text-xs" data-testid={`wf-lbl-${i}`} />
                  </div>
                  <Textarea value={s.prompt} onChange={(e) => upStep(i, "prompt", e.target.value)} placeholder="Prompt para este agente"
                            className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono text-xs min-h-[70px]" data-testid={`wf-prm-${i}`} />
                </div>
              ))}
              <Button onClick={addStep} variant="outline" className="w-full rounded-none border-dashed border-[var(--border)]" data-testid="wf-step-add">
                <Plus className="w-3 h-3 mr-2" /> Añadir paso
              </Button>
            </div>
            <DialogFooter>
              <Button onClick={create} className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="wf-submit">Crear</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-[var(--border)] p-16 text-center" data-testid="wf-empty">
          <GitBranch className="w-8 h-8 mx-auto text-[var(--text-dim)] mb-3" />
          <p className="text-sm text-[var(--text-dim)]">Sin workflows. Prueba: "Analizar lead → redactar email → aprobar CEO".</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((w) => (
            <div key={w.id} className="border border-[var(--border)] bg-[var(--surface)] p-5" data-testid={`wf-${w.id}`}>
              <div className="flex items-center justify-between mb-2">
                <Badge className="rounded-none bg-[var(--surface-2)] border border-[var(--border)] text-white hover:bg-[var(--surface-2)] font-mono text-[10px]">
                  {w.steps.length} pasos
                </Badge>
                <Button size="sm" onClick={() => run(w)} disabled={running === w.id}
                        className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80 h-8" data-testid={`wf-run-${w.id}`}>
                  <Play className="w-3 h-3 mr-1" /> {running === w.id ? "Corriendo…" : "Ejecutar"}
                </Button>
              </div>
              <div className="font-display font-bold text-lg mb-1">{w.title}</div>
              <div className="text-xs text-[var(--text-dim)] mb-3">{w.description}</div>
              <div className="space-y-1">
                {w.steps.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="font-mono text-[10px] text-[var(--accent)] uppercase w-16 truncate">{s.agent_id}</span>
                    <ArrowDown className="w-3 h-3 text-[var(--text-dim)]" />
                    <span className="text-[var(--text-dim)] flex-1 truncate">{s.label || s.prompt.slice(0, 50)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {result && (
        <div className="border border-[var(--accent)] bg-[var(--surface)] p-5" data-testid="wf-result">
          <div className="flex items-center justify-between mb-3">
            <div className="label-mono">ÚLTIMA EJECUCIÓN</div>
            <button onClick={() => setResult(null)} className="text-[var(--text-dim)] text-xs font-mono">CERRAR ✕</button>
          </div>
          <div className="space-y-3">
            {result.outputs.map((o, i) => (
              <div key={i} className="border border-[var(--border)] bg-[var(--bg)] p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="rounded-none font-mono text-[10px] bg-[var(--accent)]/10 text-[var(--accent)] border border-[var(--accent)]/40">
                    Paso {o.step + 1}
                  </Badge>
                  <span className="font-mono text-[10px] text-[var(--accent)] uppercase">{o.agent_id}</span>
                  <span className="font-mono text-[10px] text-[var(--text-dim)]">· {o.label}</span>
                </div>
                <div className="text-sm text-white whitespace-pre-wrap">{o.output}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
